import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { usePageMeta } from "@/lib/usePageMeta";
import { useI18n } from "@/contexts/I18nContext";
import { GENRES } from "@/lib/constants";
import { BookCard, BookCardSkeleton } from "@/components/BookCard";
import { ListLayout, FilterChip } from "@/components/ListLayout";
import { ListPagination } from "@/components/ListPagination";
import { ErrorState } from "@/components/StateViews";
import { AdSlot } from "@/components/AdSlot";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PAGE_SIZE = 24;
const AD_AFTER = 12; // one full-width unit after the 12th book: row 2 on desktop, row 6 on phones

export default function Books() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const genre = params.get("genre") || "";
  const sort = params.get("sort") || "newest";
  const minRating = params.get("min_rating") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);

  const qs = new URLSearchParams({ sort, page: String(page), limit: String(PAGE_SIZE) });
  if (genre) qs.set("genre", genre);
  if (minRating) qs.set("min_rating", minRating);
  const { status, data, reload } = useFetch(`/books?${qs}`);
  const items = data?.items || [];
  const total = data?.total || 0;
  usePageMeta({ title: genre ? `${genre} · ${t("all_books")}` : t("all_books"),
    canonical: `/books${genre || page > 1 ? "?" : ""}${new URLSearchParams({ ...(genre && { genre }), ...(page > 1 && { page }) })}` });
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [genre, sort, minRating, page]);

  const set = (next) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    if (!("page" in next)) p.delete("page"); // a filter or sort change starts again at page 1
    setParams(p);
  };

  const filters = (
    <div className="space-y-6">
      <fieldset>
        <legend className="mb-2 font-semibold">{t("filter_genre")}</legend>
        <ul className="flex flex-wrap gap-2 md:flex-col md:gap-0.5">
          <li><FilterChip active={!genre} onClick={() => set({ genre: "" })} testid="genre-all">{t("all")}</FilterChip></li>
          {GENRES.map((g) => (
            <li key={g}><FilterChip active={genre === g} onClick={() => set({ genre: g })} testid={`genre-${g}`}>{g}</FilterChip></li>
          ))}
        </ul>
      </fieldset>
      <fieldset>
        <legend className="mb-2 font-semibold">{t("filter_rating")}</legend>
        <ul className="flex flex-wrap gap-2 md:flex-col md:gap-0.5">
          {[["", t("rating_any")], ["4", t("rating_4")], ["3", t("rating_3")]].map(([v, label]) => (
            <li key={v || "any"}><FilterChip active={minRating === v} onClick={() => set({ min_rating: v })} testid={`rating-${v || "any"}`}>{label}</FilterChip></li>
          ))}
        </ul>
      </fieldset>
    </div>
  );

  const sortControl = (
    <Select value={sort} onValueChange={(v) => set({ sort: v })}>
      <SelectTrigger className="w-44" aria-label={t("sort_by")} data-testid="sort-select"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="newest">{t("sort_newest")}</SelectItem>
        <SelectItem value="rating">{t("sort_rating")}</SelectItem>
        <SelectItem value="reviewed">{t("sort_reviewed")}</SelectItem>
      </SelectContent>
    </Select>
  );

  const active = [
    genre && { key: "genre", label: genre },
    minRating && { key: "min_rating", label: minRating === "4" ? t("rating_4") : t("rating_3") },
  ].filter(Boolean);

  return (
    <ListLayout
      title={genre || t("all_books")}
      count={status === "ok" ? t("count_books").replace("{n}", total) : null}
      filters={filters} sort={sortControl}
      active={active} onRemove={(k) => set({ [k]: "" })}
    >
      {status === "error" ? <ErrorState onRetry={reload} />
        : status === "ok" && items.length === 0 ? (
          <p className="py-10 text-muted-foreground" data-testid="books-empty">{t("no_books")}</p>
        ) : (
          <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4 sm:gap-x-6" data-testid="books-grid">
            {status !== "ok"
              ? Array.from({ length: 12 }, (_, i) => <li key={i}><BookCardSkeleton /></li>)
              : items.map((b, i) => (
                <React.Fragment key={b.id}>
                  <li><BookCard book={b} /></li>
                  {i === AD_AFTER - 1 && items.length > AD_AFTER && (
                    <li className="col-span-full"><AdSlot placement="list-inline" /></li>
                  )}
                </React.Fragment>
              ))}
          </ul>
        )}
      {status === "ok" && <ListPagination page={page} limit={PAGE_SIZE} total={total} onPageChange={(p) => set({ page: p > 1 ? String(p) : "" })} testid="books-pagination" />}
    </ListLayout>
  );
}
